import { Component, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import {
  AlertService,
  LocationService,
  RacunService,
  SharedService,
} from '@kodit/core/services';
import {
  CreateUlaznaFakturaCommand,
  GetUlaznaFakturaQuery,
  IUlaznaFakturaDto,
  RacuniClient,
  TipRacuna,
  UlazneFaktureClient,
  UpdateUlaznaFakturaCommand,
  VrstaFakture,
  ZavisniTrosakStavkaDto,
} from '@kodit/core/data-api';
import { ConfirmationService } from 'primeng/api';
import {
  FormHelper,
  UlaznaFakturaFormService,
} from 'libs/core/form-definitions/src';
import { ActivatedRoute } from '@angular/router';
import { RacunFormConfig } from '../../subforms/racun-form-container/racun-form.config';
import { FormGroupTypeSafe } from 'angular-typesafe-reactive-forms-helper';
import { FormArray } from '@angular/forms';
import { BreadcrumbService } from 'xng-breadcrumb';
import { finalize, map } from 'rxjs/operators';

@Component({
  selector: 'kodit-ulazna-faktura-form',
  templateUrl: './ulazna-faktura-form.component.html',
  styleUrls: ['./ulazna-faktura-form.component.scss'],
})
export class UlaznaFakturaFormComponent implements OnInit, OnDestroy {
  /** Subs */
  private _subs: Subscription = new Subscription();
  private _saveSub: Subscription = new Subscription();
  private _errorSub: Subscription = new Subscription();
  private _valutaChangedSub: Subscription = new Subscription();

  /** Props */
  isLoading = false;
  readonly skeletonRows = Array(3).fill(null);
  readonly skeletonFields = Array(5).fill(null);
  fakturaForm: FormGroupTypeSafe<IUlaznaFakturaDto>;
  racunFormConfig: RacunFormConfig;

  constructor(
    private _client: UlazneFaktureClient,
    private _racuniClient: RacuniClient,
    private _alertService: AlertService,
    private _confirmationService: ConfirmationService,
    private _locationService: LocationService,
    private _racunService: RacunService,
    private _formService: UlaznaFakturaFormService,
    private _route: ActivatedRoute,
    private _formHelper: FormHelper,
    private _breadcrumbService: BreadcrumbService,
    private _sharedService : SharedService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    const editId = Number.parseInt(this._route.snapshot.paramMap.get('id'));
    const formConfig$ = this._racuniClient.getFormConfiguration().pipe(map((x) => x.data));

    if (editId) {
      this._subs.add(
        forkJoin([
          formConfig$,
          this._client.get(new GetUlaznaFakturaQuery({ id: editId })).pipe(map((x) => x.data)),
        ])
          .pipe(finalize(() => { this.isLoading = false; }))
          .subscribe(([formConfig, racun]) => {
            this.initializeForm(formConfig, racun);
          })
      );
    } else {
      this._subs.add(
        formConfig$
          .pipe(finalize(() => { this.isLoading = false; }))
          .subscribe((formConfig) => {
            this.initializeForm(formConfig, this._route.snapshot.data.racun);
          })
      );
    }
  }

  get jeEditMode(): boolean {
    return !!this.fakturaForm?.controls.racunDto?.value?.id;
  }

  private initializeForm(formConfig: any, racun?: any): void {
    this.fakturaForm = this._formService.GetUlaznaFakturaFormGroup(
      racun,
      formConfig
    );
    // configure labels
    this._racunService.setBrojRacunaLabel = 'Broj fakture';
    this.racunFormConfig = {
      tip: TipRacuna.ULAZNA_FAKTURA,
      racunForm: this.fakturaForm.controls.racunDto,
      brojRacunaTooltip: 'Dodeljen broj fakture',
      originalanBrojRacunaTooltip:
        'Unesite broj fakture koji ste dobili od izdavaoca',
      saveCallback: () => this.onSave(),
      cancelCallback: () => this.onCancel(),
    };

    this._valutaChangedSub = this._racunService.getJeDomaciRacun.subscribe(
      (val) => {
        this.fakturaForm.controls.vrstaFakture.patchValue(
          val ? VrstaFakture.DOMACA : VrstaFakture.STRANA
        );
      }
    );
    this._setBreadcrumb();

    this._racunService.resetZavisneTroskovi();
    this._racunService.addZavisneTroskoveFromUf(this.fakturaForm.value.zavisniTroskoviStavkeDto);
  }

  onSave() {
    this._formHelper.removeEmptyStavka(
      this.fakturaForm.controls.racunDto.get('stavkeDto') as FormArray
    );
    // check is it valid
    if (!this.fakturaForm.valid) {
      this._formHelper.invalidateForm(this.fakturaForm);
      return;
    }

    if (this._racunService.proveraIdentifikatora()) {
      this._alertService.addWarnMsg("Račun ne sme sadržati istovremeno stavke koje se odnose i koje se ne odnose na promet nekretnina");
      return;
    }
    
    

    if (this.jeEditMode) {
      this._sharedService.displayLoadingScreen(true, 'Ažuriranje ulazne fakture...');
      
      const request = this.fakturaForm.value as UpdateUlaznaFakturaCommand;
      request.zavisniTroskoviStavkeDto = this._racunService
        .getZavisneTroskove as ZavisniTrosakStavkaDto[];
      
      this._saveSub = this._client
        .update(
          this.fakturaForm.value.racunDto.id,
          request
        )
        .pipe(
          finalize(() => {
            this._sharedService.resetLoadingScreen();
          })
        )
        .subscribe(() => {
          this._alertService.addSuccessMsg(
            `Faktura ${this.fakturaForm.value.racunDto.broj} je uspešno izmenjena.`
          );
          this._locationService.routeBack();
        });
    } else {
      this._sharedService.displayLoadingScreen(true, 'Kreiranje ulazne fakture...');

      const request = this.fakturaForm.value as CreateUlaznaFakturaCommand;
      request.zavisniTroskoviStavkeDto = this._racunService
        .getZavisneTroskove as ZavisniTrosakStavkaDto[];

      this._saveSub = this._client
        .create(request)
        .pipe(
          finalize(() => {
            this._sharedService.resetLoadingScreen();
          })
        )
        .subscribe(() => {
          this._alertService.addSuccessMsg(`Faktura je uspešno kreirana.`);
          this._locationService.routeBack();
        });
    }
  }

  onCancel() {
    this._confirmationService.confirm({
      message: 'Ukoliko otkažete sav ne snimljen sadržaj će biti izgubljen.',
      acceptLabel: 'Ipak otkaži',
      rejectLabel: 'Ostani na stranici',
      header: 'Potvrdite otkazivanje',
      icon: 'far fa-check-circle',
      rejectButtonStyleClass: 'p-button-outlined',
      accept: () => {
        this._locationService.routeBack();
      },
    });
  }

  private _setBreadcrumb() {
    this._breadcrumbService.set(
      '@unosUlazneFaktureZaStranku',
      `Unos ulazne fakture za '${this.fakturaForm.value.racunDto.strankaRacunaDto.naziv}'`
    );
    this._breadcrumbService.set(
      '@izmenaUlazneFakture',
      `Izmena ulazne fakture ${this.fakturaForm.value.racunDto.broj}`
    );
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
    this._saveSub.unsubscribe();
    this._errorSub.unsubscribe();
    this._valutaChangedSub.unsubscribe();
  }
}
